// src/components/resources/tools/generators/shared/usePersistedGeneratorConfig.ts

'use client'

import { useCallback, useEffect } from 'react'

const STORAGE_PREFIX = 'adsights_generator_config_v1_'

/**
 * Persists a generator config to localStorage with debounced writes.
 * Large binary fields (data URLs) are stripped before save to avoid quota errors.
 *
 * Writes are gated by `canPersist` so restore-on-mount cannot be clobbered by
 * default state and untouched sessions are not saved as bogus drafts.
 *
 * @template T
 * @param {string} storageKey - Unique key suffix (usually tracking slug).
 * @param {T} config - Live config state.
 * @param {(value: T) => T} serialize - Strip non-serializable / heavy fields.
 * @param {Object} [options] - Persistence options.
 * @param {boolean} [options.canPersist=false] - When true, debounced writes run.
 * @param {number} [options.debounceMs=600] - Debounce interval for writes.
 * @returns Restore helpers for the generator.
 */
export function usePersistedGeneratorConfig<T>(
  storageKey: string,
  config: T,
  serialize: (value: T) => T,
  options: {
    canPersist?: boolean
    debounceMs?: number
  } = {},
): {
  clearPersisted: () => void
} {
  const { canPersist = false, debounceMs = 600 } = options
  const fullKey = `${STORAGE_PREFIX}${storageKey}`

  const clearPersisted = useCallback((): void => {
    try {
      localStorage.removeItem(fullKey)
    } catch {
      // ignore
    }
  }, [fullKey])

  useEffect(() => {
    if (!canPersist) return
    const handle = window.setTimeout(() => {
      try {
        const payload = serialize(config)
        localStorage.setItem(fullKey, JSON.stringify(payload))
      } catch {
        // Quota or private mode — silent no-op.
      }
    }, debounceMs)
    return () => window.clearTimeout(handle)
  }, [canPersist, config, debounceMs, fullKey, serialize])

  return { clearPersisted }
}

/**
 * Reads persisted config once on mount.
 *
 * @template T
 * @param {string} storageKey - Unique key suffix.
 * @param {(raw: unknown) => T | null} parse - Validates stored JSON.
 * @returns {T | null} Parsed config or null.
 */
export function readPersistedGeneratorConfig<T>(
  storageKey: string,
  parse: (raw: unknown) => T | null,
): T | null {
  const fullKey = `${STORAGE_PREFIX}${storageKey}`
  try {
    const raw = localStorage.getItem(fullKey)
    if (!raw) return null
    return parse(JSON.parse(raw) as unknown)
  } catch {
    return null
  }
}

/**
 * Removes a corrupted or invalid persisted draft.
 *
 * @param {string} storageKey - Unique key suffix.
 */
export function clearPersistedGeneratorConfig(storageKey: string): void {
  const fullKey = `${STORAGE_PREFIX}${storageKey}`
  try {
    localStorage.removeItem(fullKey)
  } catch {
    // ignore
  }
}
